import { useRestaurantEnabled } from '@/features/business/api';
import { ChevronDown, ChevronLeft, ExternalLink, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { useAuthStore } from '@/shared/auth/auth-store';
import { cn } from '@/shared/lib/cn';

import { Logo } from './Logo';
import { navConfig, type NavItem, type NavGroup } from './nav-config';

interface Props {
  collapsed: boolean;
  onToggle: () => void;
  /** Whether the off-canvas drawer is open (mobile/tablet only). */
  mobileOpen?: boolean;
  /** Close the off-canvas drawer (mobile/tablet only). */
  onMobileClose?: () => void;
}

const STORAGE_KEY = 'elshahd_sidebar_open_group';

function visibleForUser(
  item: NavItem,
  has: (p: string | string[]) => boolean,
  bypass: boolean,
): boolean {
  if (!item.permission) return true;
  if (bypass) return true;
  return has(item.permission);
}

/**
 * Returns the **single** `to` (path) that should be highlighted as active for
 * the current location — the longest matching prefix across every navigation
 * item. Falls back to an exact match or null when nothing fits. This avoids
 * lighting up `/app/purchases` while the user is on `/app/purchases/new`.
 */
function findActivePath(groups: NavGroup[], pathname: string): string | null {
  let best: string | null = null;
  const consider = (to: string) => {
    if (pathname === to || pathname.startsWith(to + '/')) {
      if (!best || to.length > best.length) best = to;
    }
  };
  for (const g of groups) {
    if (g.to) consider(g.to);
    for (const it of g.items) {
      if (it.to) consider(it.to);
    }
  }
  return best;
}

/** Returns the group key (label) that owns the currently active route. */
function findActiveGroup(groups: NavGroup[], pathname: string): string | null {
  const active = findActivePath(groups, pathname);
  if (!active) return null;
  for (const g of groups) {
    if (g.to === active) return g.group;
    if (g.items.some((it) => it.to === active)) return g.group;
  }
  return null;
}

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const permissions = useAuthStore((s) => s.permissions);
  const bypass = permissions.size === 0;
  // Case-insensitive match — backend slugs are inconsistently cased (Pos_view vs pos_view).
  const lowerPerms = new Set<string>();
  for (const p of permissions) lowerPerms.add(p.toLowerCase());
  const hasOne = (p: string) => lowerPerms.has(p.toLowerCase());
  const has = (p: string | string[]) => (Array.isArray(p) ? p.some(hasOne) : hasOne(p));

  // Single-open accordion: either one group is open, or none.
  const [openKey, setOpenKey] = useState<string | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return raw;
    } catch {
      /* ignore */
    }
    return 'Sales';
  });

  // Single active path (longest prefix) — only this item is highlighted.
  const activePath = useMemo(
    () => findActivePath(navConfig, location.pathname),
    [location.pathname],
  );

  // Active group based on current route — auto-open it (closing whatever was
  // open before).
  const activeGroup = useMemo(
    () => findActiveGroup(navConfig, location.pathname),
    [location.pathname],
  );
  useEffect(() => {
    if (activeGroup) setOpenKey(activeGroup);
  }, [activeGroup]);

  // Persist
  useEffect(() => {
    try {
      if (openKey == null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, openKey);
    } catch {
      /* ignore */
    }
  }, [openKey]);

  const toggleGroup = (key: string) => setOpenKey((prev) => (prev === key ? null : key));

  const restaurantEnabled = useRestaurantEnabled();
  const [filter, setFilter] = useState('');

  // While filtering, "open all" by setting a sentinel — we'll handle this in
  // the render by treating filter !== '' as override.
  const filterMode = filter.trim().length > 0;

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-e border-sidebar-border bg-sidebar text-sidebar-foreground',
        // Off-canvas drawer below lg; static column on lg+
        'fixed inset-y-0 start-0 z-50 transition-[width,transform] duration-200 lg:static lg:z-auto',
        // Width: full drawer on mobile, collapse rules apply on lg+
        collapsed ? 'w-[280px] lg:w-[76px]' : 'w-[280px] lg:w-[270px]',
        // Slide in/out below lg only; no transform at lg+ (always visible)
        mobileOpen
          ? 'max-lg:translate-x-0'
          : 'max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full',
      )}
    >
      {restaurantEnabled && has('Pos_view') && <NavLink to="/app/restaurant" onClick={onMobileClose} className="order-1 mx-3 mb-2 rounded-lg border border-sidebar-border px-3 py-3 text-center text-sm font-semibold">{t('nav.restaurant', 'الطاولات والمطبخ')}</NavLink>}
      {/* Brand */}
      <div className="relative flex h-16 items-center justify-center border-b border-sidebar-border px-3">
        <Link
          to="/app/dashboard"
          aria-label={t('nav.dashboard', 'لوحة التحكم')}
          title={t('nav.dashboard', 'لوحة التحكم')}
          className="flex items-center justify-center rounded-lg transition hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {collapsed ? (
            <Logo variant="mark" theme="dark" height={28} />
          ) : (
            <Logo variant="full" theme="dark" height={30} />
          )}
        </Link>
        {/* Desktop: collapse/expand toggle */}
        <button
          onClick={onToggle}
          className="absolute end-2 top-1/2 hidden h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground lg:grid"
          aria-label="toggle sidebar"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <ChevronLeft
            className={cn(
              'h-4 w-4 transition-transform rtl:rotate-180',
              collapsed && 'rotate-180 rtl:rotate-0',
            )}
          />
        </button>
        {/* Mobile/tablet: close drawer */}
        <button
          onClick={onMobileClose}
          className="absolute end-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground lg:hidden"
          aria-label="close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Quick filter (only when expanded) */}
      {!collapsed && (
        <div className="border-b border-sidebar-border px-3 py-2">
          <div className="relative">
            <Search className="absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-sidebar-foreground/50" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('nav.search', 'Filter menu…')}
              className="h-8 w-full rounded-md border border-sidebar-border/40 bg-sidebar-foreground/[0.05] ps-8 pe-7 text-xs text-sidebar-foreground placeholder:text-sidebar-foreground/40 focus:bg-sidebar-foreground/[0.08] focus:outline-none focus:ring-1 focus:ring-sidebar-foreground/20"
            />
            {filter && (
              <button
                onClick={() => setFilter('')}
                className="absolute end-2 top-1/2 -translate-y-1/2 text-sidebar-foreground/50 hover:text-sidebar-foreground"
                aria-label="clear filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="sidebar-scroll flex-1 overflow-y-auto px-2 py-3">
        <ul className="flex flex-col gap-0.5">
          {navConfig.map((group) => {
            const filterTerm = filter.trim().toLowerCase();

            // Standalone link group (no children, renders as direct NavLink).
            if (group.to) {
              if (group.permission && !bypass && !has(group.permission)) {
                return null;
              }
              const label = t(group.i18nKey ?? group.group, group.group);
              if (filterTerm && !label.toLowerCase().includes(filterTerm)) {
                return null;
              }
              if (collapsed) {
                return (
                  <li key={group.group} className="mb-1">
                    <CollapsedItem
                      item={{
                        label: group.group,
                        ...(group.i18nKey && { i18nKey: group.i18nKey }),
                        to: group.to,
                        ...(group.icon && { icon: group.icon }),
                      }}
                      t={t}
                    />
                  </li>
                );
              }
              return (
                <li key={group.group}>
                  <NavLink
                    to={group.to}
                    className={cn(
                      'group/g flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm font-semibold transition-colors',
                      'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                      activePath === group.to && 'bg-sidebar-accent text-sidebar-foreground',
                    )}
                  >
                    {(() => {
                      const isActive = activePath === group.to;
                      return (
                        <>
                          {group.icon && (
                            <span
                              className={cn(
                                'grid h-7 w-7 flex-shrink-0 place-items-center rounded-md transition-colors',
                                isActive
                                  ? 'bg-sidebar-foreground/10 text-sidebar-foreground'
                                  : 'bg-sidebar-foreground/[0.05] text-sidebar-foreground/75 group-hover/g:bg-sidebar-foreground/[0.06] group-hover/g:text-sidebar-foreground',
                              )}
                            >
                              <group.icon className="h-4 w-4" />
                            </span>
                          )}
                          <span className="truncate">{label}</span>
                        </>
                      );
                    })()}
                  </NavLink>
                </li>
              );
            }

            let items = group.items.filter((it) => visibleForUser(it, has, bypass));
            if (filterTerm) {
              items = items.filter((it) => {
                const label = t(it.i18nKey ?? it.label, it.label).toLowerCase();
                return (
                  label.includes(filterTerm) ||
                  it.label.toLowerCase().includes(filterTerm) ||
                  group.group.toLowerCase().includes(filterTerm)
                );
              });
            }
            // Drop orphan section-headers whose following items are all hidden.
            items = items.filter((it, i) => {
              if (!it.header) return true;
              for (let j = i + 1; j < items.length; j++) {
                if (items[j]!.header) return false;
                return true;
              }
              return false;
            });
            // Hide the whole group when only headers remain or nothing visible.
            if (items.length === 0 || items.every((it) => it.header)) return null;

            const isOpen = filterMode ? true : openKey === group.group;
            const isActive = activeGroup === group.group;

            // Collapsed mode: flat icon list with hover flyout
            if (collapsed) {
              return (
                <li key={group.group} className="mb-1">
                  {items.map((it) => (
                    <CollapsedItem key={it.to} item={it} t={t} />
                  ))}
                </li>
              );
            }

            return (
              <li key={group.group}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.group)}
                  className={cn(
                    'group/g flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-sm font-semibold transition-colors',
                    'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                    isActive && 'bg-sidebar-accent/40 text-sidebar-foreground',
                  )}
                  aria-expanded={isOpen}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    {group.icon && (
                      <span
                        className={cn(
                          'grid h-7 w-7 flex-shrink-0 place-items-center rounded-md transition-colors',
                          isActive
                            ? 'bg-sidebar-foreground/10 text-sidebar-foreground'
                            : 'bg-sidebar-foreground/[0.05] text-sidebar-foreground/75 group-hover/g:bg-sidebar-foreground/[0.06] group-hover/g:text-sidebar-foreground',
                        )}
                      >
                        <group.icon className="h-4 w-4" />
                      </span>
                    )}
                    <span className="truncate">
                      {t(group.i18nKey ?? group.group, group.group)}
                    </span>
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 flex-shrink-0 text-sidebar-foreground/60 transition-transform duration-200',
                      isOpen && 'rotate-180',
                    )}
                  />
                </button>

                {/* Collapsible body */}
                <div
                  className={cn(
                    'grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-out',
                    isOpen
                      ? 'grid-rows-[1fr] opacity-100'
                      : 'grid-rows-[0fr] pointer-events-none opacity-0',
                  )}
                  aria-hidden={!isOpen}
                >
                  <ul className="relative min-h-0 overflow-hidden ps-5 pt-1 pb-2">
                    {/* Vertical guide line */}
                    <span
                      className="pointer-events-none absolute start-[1.4rem] top-1 bottom-2 w-px bg-sidebar-foreground/[0.06]"
                      aria-hidden
                    />
                    {items.map((it, idx) => {
                      // Non-clickable section header inside a dropdown
                      if (it.header) {
                        return (
                          <li
                            key={`hdr-${idx}-${it.label}`}
                            className="relative mt-2 first:mt-0 px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/45"
                          >
                            {t(it.i18nKey ?? it.label, it.label)}
                          </li>
                        );
                      }
                      const a = activePath === it.to;
                      return (
                        <li key={it.to ?? `i-${idx}`} className="relative">
                          <NavLink
                            to={it.to ?? '#'}
                            className={cn(
                              'group/i relative flex items-center gap-2.5 rounded-md py-2 ps-3 pe-3 text-[13px] transition-colors',
                              'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                              a && 'bg-sidebar-accent text-sidebar-foreground',
                            )}
                          >
                            {(() => (
                              <>
                                <span
                                  className={cn(
                                    'grid h-6 w-6 flex-shrink-0 place-items-center rounded-md transition-colors',
                                    a
                                      ? 'bg-sidebar-foreground/10 text-sidebar-foreground'
                                      : 'bg-sidebar-foreground/[0.04] text-sidebar-foreground/70 group-hover/i:bg-sidebar-foreground/[0.06] group-hover/i:text-sidebar-foreground',
                                  )}
                                >
                                  {it.icon ? (
                                    <it.icon className="h-3.5 w-3.5" />
                                  ) : (
                                    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                                  )}
                                </span>
                                <span className="truncate">
                                  {t(it.i18nKey ?? it.label, it.label)}
                                </span>
                              </>
                            ))()}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </li>
            );
          })}
        </ul>
      </nav>

      {!collapsed && (
        <div className="border-t border-sidebar-border p-4">
          <a
            href="https://tarawud.com"
            target="_blank"
            rel="noreferrer"
            className="group flex items-center justify-center gap-1.5 text-xs font-semibold text-sidebar-foreground/80 transition-colors hover:text-primary"
          >
            tarawud.com
            <ExternalLink className="h-3 w-3 opacity-60 transition-opacity group-hover:opacity-100" />
          </a>
          <p className="mt-2 text-center text-[10px] leading-relaxed text-sidebar-foreground/45">
            {t('sidebar.copyright', 'جميع الحقوق محفوظة لشركة Tarawud')}
          </p>
          <div className="mt-1.5 flex justify-center">
            <span className="rounded-full bg-sidebar-accent px-2 py-0.5 text-[10px] font-semibold text-sidebar-foreground/60">
              v1.0
            </span>
          </div>
        </div>
      )}
    </aside>
  );
}

// ───────────────────────────── helpers ──────────────────────────────────────

function CollapsedItem({
  item,
  t,
}: {
  item: NavItem;
  t: (k: string, d: string) => string;
}) {
  if (!item.to) return null;
  return (
    <NavLink
      to={item.to}
      title={t(item.i18nKey ?? item.label, item.label)}
      className={({ isActive }) =>
        cn(
          'group relative my-0.5 flex h-10 items-center justify-center rounded-md text-sidebar-foreground/75 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground',
          isActive && 'bg-sidebar-accent text-sidebar-foreground',
        )
      }
    >
      {item.icon && <item.icon className="h-4 w-4" />}
      {/* Flyout label on hover */}
      <span className="pointer-events-none absolute start-full top-1/2 z-50 ms-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-popover px-2.5 py-1 text-xs font-medium text-popover-foreground shadow-md group-hover:block">
        {t(item.i18nKey ?? item.label, item.label)}
      </span>
    </NavLink>
  );
}
